import { useEffect } from "react";

// How far the mouse must travel before a press becomes a drag. Below it the
// press is still a click, so a row's link keeps working under a hand that
// wobbled.
const DRAG_THRESHOLD = 5;

// Let a mouse pull the chart sideways.
//
// The frame always scrolled, but only by a horizontal wheel or its scrollbar,
// and the scrollbar sits at the foot of a chart that is as tall as its
// chronology — several screens down. With an ordinary wheel there was no
// visible way to move along the years at all. Touch already pans natively, so
// this answers the mouse only.
//
// Like the pinning in TimelineSvg, it writes to the DOM rather than to React
// state: a re-render of every row on every pixel of a drag is a chart that
// stutters in the hand.
export function useDragToScroll(scrollRef, svgRef) {
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;

    let drag = null;
    // A drag that ends over a link must not then follow it.
    let swallowClick = false;

    const updatePannable = () => {
      scroller.dataset.pannable = scroller.scrollWidth > scroller.clientWidth ? "true" : "false";
    };

    const end = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      swallowClick = drag.moved;
      drag = null;
      delete scroller.dataset.dragging;
    };

    const onPointerDown = (event) => {
      swallowClick = false;
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startLeft: scroller.scrollLeft,
        moved: false
      };
    };

    const onPointerMove = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const dx = event.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.moved = true;
        scroller.setPointerCapture(event.pointerId);
        scroller.dataset.dragging = "true";
        window.getSelection()?.removeAllRanges();
      }
      scroller.scrollLeft = drag.startLeft - dx;
    };

    const onClick = (event) => {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    // The rows are links, and a browser drags a link as a link — which ends the
    // pointer stream and leaves the chart where it was.
    const onDragStart = (event) => event.preventDefault();

    scroller.addEventListener("pointerdown", onPointerDown);
    scroller.addEventListener("pointermove", onPointerMove);
    scroller.addEventListener("pointerup", end);
    scroller.addEventListener("pointercancel", end);
    scroller.addEventListener("click", onClick, true);
    scroller.addEventListener("dragstart", onDragStart);

    // A zoom changes the SVG's width without touching the frame's, so both are
    // watched.
    updatePannable();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePannable);
    observer?.observe(scroller);
    if (svgRef.current) observer?.observe(svgRef.current);

    return () => {
      scroller.removeEventListener("pointerdown", onPointerDown);
      scroller.removeEventListener("pointermove", onPointerMove);
      scroller.removeEventListener("pointerup", end);
      scroller.removeEventListener("pointercancel", end);
      scroller.removeEventListener("click", onClick, true);
      scroller.removeEventListener("dragstart", onDragStart);
      observer?.disconnect();
    };
  }, [scrollRef, svgRef]);
}
